import type { AchievementDef } from "../engine/AchievementEngine";

interface AchievementGalleryProps {
  achievements: Record<string, AchievementDef>;
  unlocked: Set<string>;
  onClose: () => void;
}

export function AchievementGallery({ achievements, unlocked, onClose }: AchievementGalleryProps) {
  const entries = Object.entries(achievements);
  const unlockedCount = entries.filter(([id]) => unlocked.has(id)).length;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0, 0, 0, 0.82)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        zIndex: 95,
        overflowY: "auto",
        padding: "2rem 1rem",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="theme-panel"
        style={{ width: "min(720px, 96%)", padding: "1.5rem 2rem", borderRadius: "8px" }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 400, letterSpacing: "0.04em" }}>
              Achievements
            </h2>
            <div style={{ fontSize: "0.75rem", opacity: 0.5, marginTop: "0.2rem" }}>
              {unlockedCount} / {entries.length} unlocked
            </div>
          </div>
          <button
            onClick={onClose}
            className="theme-button"
            style={{ padding: "0.3rem 0.8rem", fontSize: "0.85rem" }}
          >
            Close
          </button>
        </div>

        {/* Achievement grid */}
        {entries.length === 0 ? (
          <div style={{ textAlign: "center", opacity: 0.4, padding: "2rem" }}>
            No achievements defined for this story.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "0.75rem",
            }}
          >
            {entries.map(([id, ach]) => {
              const isUnlocked = unlocked.has(id);
              const isHidden = ach.hidden && !isUnlocked;
              return (
                <div
                  key={id}
                  style={{
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "6px",
                    padding: "0.75rem",
                    display: "flex",
                    gap: "0.75rem",
                    alignItems: "flex-start",
                    opacity: isUnlocked ? 1 : 0.45,
                    background: isUnlocked ? "rgba(255,255,255,0.04)" : "transparent",
                    transition: "opacity 0.2s",
                  }}
                >
                  {/* Icon or placeholder */}
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      flexShrink: 0,
                      borderRadius: "4px",
                      background: "rgba(255,255,255,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.1rem",
                      overflow: "hidden",
                    }}
                  >
                    {isHidden ? (
                      <span style={{ opacity: 0.4 }}>?</span>
                    ) : ach.icon ? (
                      <img src={ach.icon} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    ) : (
                      <span style={{ opacity: 0.35 }}>{isUnlocked ? "★" : "○"}</span>
                    )}
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.85rem", fontWeight: 500, marginBottom: "0.2rem" }}>
                      {isHidden ? "???" : ach.title}
                    </div>
                    <div style={{ fontSize: "0.72rem", opacity: 0.55, lineHeight: 1.4 }}>
                      {isHidden ? "???" : ach.desc}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
